import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  PageContentPreloader,
  PageLayout,
  PageTabs,
  TranslatedNavLink,
  Calendar,
  NoItemsPanel,
} from 'common/components';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTheme } from 'common/styling/theme';
import { useRouteMatch } from 'react-router-dom';

import TradeOperationsTable from 'client/_components/TradeOperationsTable';
import noOrdersImage from 'common/images/noOrders.svg';

import { InvestStatementPanel, InvestmentInfoPanel } from '../../_components';

import { getTradingAccountList } from '../../../_redux';
import { getInvestStatement, getInvestmentTrades } from '../../_redux';

import { staticStyles, getDynamicStyles } from './style';

const startDate = moment().startOf('day').unix();

const endDate = moment().endOf('day').unix();

const startCalendar = moment().startOf('day');

const endCalendar = moment().endOf('day');

const InvestStatementPage = ({
  investStatement,
  investmentTrades,
  investStatementIsLoaded,
  investmentTradesAreLoaded,
  getInvestStatement,
  getInvestmentTrades,
  getTradingAccountList,
  theme,
}) => {
  const match = useRouteMatch();
  const {
    params: { investmentId, tradeType },
  } = match;

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  useEffect(() => {
    getTradingAccountList();
  }, [getTradingAccountList]);

  useEffect(() => {
    getInvestStatement({ investmentId });
  }, [getInvestStatement, investmentId]);

  const getList = useCallback(() => {
    if (investStatement?.account?.platformSlug && Number(investmentId) === investStatement.id) {
      getInvestmentTrades({
        investmentId,
        tradeType,
        startDate,
        endDate,
        accountPlatformSlug: investStatement?.account?.platformSlug,
      });
    }
  }, [getInvestmentTrades, investmentId, investStatement, tradeType]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleChangeDate = (start, end) => {
    getInvestmentTrades({
      investmentId,
      tradeType,
      startDate: start,
      endDate: end,
      accountPlatformSlug: investStatement?.account?.platformSlug,
    });
  };

  return (
    <PageLayout captionSlug="justInvestStatement" showReload={false}>
      <PageContentPreloader ready={investStatementIsLoaded} type="bigLine" firstLaunchOnly>
        <InvestStatementPanel investStatement={investStatement} investmentId={investmentId} />
        <InvestmentInfoPanel investStatement={investStatement} />
        <>
          <div className="InvestStatementPage__top-line">
            <PageTabs>
              <TranslatedNavLink id="justOpen" to={`/invest/investments/${investmentId}/open`} />

              <TranslatedNavLink id="justClosed" to={`/invest/investments/${investmentId}/closed`} />
            </PageTabs>
            <div className="InvestStatementPage__date">
              {tradeType === 'closed' && (
                <Calendar
                  daysLimit={30}
                  onChangeFunc={handleChangeDate}
                  defaultStartDate={startCalendar}
                  defaultEndDate={endCalendar}
                  setCorrectLastDay
                />
              )}
            </div>
          </div>
          <div className="InvestStatementPage__table">
            <PageContentPreloader ready={investmentTradesAreLoaded} type="smallLines">
              {investmentTrades.items?.length ? (
                <TradeOperationsTable
                  data={investmentTrades.items}
                  summary={investmentTrades.summary}
                  operationStatus={tradeType}
                  isClient
                />
              ) : (
                <NoItemsPanel icon={noOrdersImage} captionSlug="yotHaveNoOrdersYet" />
              )}
            </PageContentPreloader>
          </div>
        </>
      </PageContentPreloader>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </PageLayout>
  );
};

InvestStatementPage.propTypes = {
  investStatement: PropTypes.object.isRequired,
  investmentTrades: PropTypes.object.isRequired,
  investStatementIsLoaded: PropTypes.bool.isRequired,
  investmentTradesAreLoaded: PropTypes.bool.isRequired,
  getInvestStatement: PropTypes.func.isRequired,
  getInvestmentTrades: PropTypes.func.isRequired,
  getTradingAccountList: PropTypes.func.isRequired,
  theme: PropTypes.object,
};

InvestStatementPage.defaultProps = {
  theme: {},
};

export default compose(
  withTheme(),
  connect(
    state => ({
      investStatement: state.client.invest.investStatement,
      investStatementIsLoaded: state.client.invest.investStatementIsLoaded,
      investmentTrades: state.client.invest.investmentTrades,
      investmentTradesAreLoaded: state.client.invest.investmentTradesAreLoaded,
    }),
    {
      getInvestStatement: data => getInvestStatement.request(data),
      getInvestmentTrades: data => getInvestmentTrades.request(data),
      getTradingAccountList: () => getTradingAccountList.request({ noDetails: true }),
    }
  )
)(InvestStatementPage);
export { InvestStatementPage };
